// Single Card Feed Styles
// Reusable styles for single-card snap scroll view across all feed tabs

import styled from "styled-components";
import { theme } from "../theme";

// Main Container
export const SingleCardFeedContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100vh;
  background: ${theme.colors.background.primary};
  overflow: hidden;

  @media (max-width: ${theme.breakpoints.mobile}) {
    height: 100dvh;
  }
`;

// Scroll Container - One card at a time with snap scrolling
// Positioned to account for top navigation (header + tabs)
export const SingleCardScrollContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow-y: scroll;
  overflow-x: hidden;
  scroll-behavior: smooth;
  scroll-snap-type: y mandatory;
  /* Account for bottom navigation: ~70px - prevents last card from being hidden */
  scroll-padding-bottom: 70px;
  scroll-padding-top: 0;
  /* No top padding - cards extend to top, tabs overlay transparently */
  padding-top: 0;
  padding-bottom: 0;
  padding-left: 0;
  padding-right: 0;
  margin: 0;
  width: 100%;
  /* Height is determined by absolute positioning (top: 0, bottom: 0) */
  /* Prevent any gaps between cards */
  gap: 0;
  /* Ensure cards snap perfectly with no gaps */
  scroll-snap-stop: always;
  /* Improve scroll performance */
  will-change: scroll-position;
  transform: translateZ(0);
  /* Ensure scrolling is enabled */
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  /* Allow touch scrolling */
  touch-action: pan-y;

  /* Hide scrollbar */
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    /* Mobile bottom nav: ~70px */
    scroll-padding-bottom: 70px;
    scroll-padding-top: 0;
    padding-top: 0;
    /* Height is determined by absolute positioning (top: 0, bottom: 0) */
  }
  
  /* Ensure children (cards) have no gaps and fill exactly */
  > * {
    margin: 0;
    padding: 0;
    flex-shrink: 0;
    scroll-snap-align: start;
    scroll-snap-stop: always;
  }
`;

// Single Card Wrapper - Same dimensions as MatchCard
export const SingleCardWrapper = styled.div`
  position: relative;
  width: 100%;
  /* Desktop: Full viewport height - card fills entire screen, tabs overlay transparently */
  height: 100vh;
  min-height: 100vh;
  max-height: 100vh;
  margin: 0;
  padding: 0;
  scroll-snap-align: start;
  scroll-snap-stop: always;
  /* Ensure no margin or gap that would allow next card to peek */
  margin-bottom: 0;
  flex-shrink: 0;
  /* Improve scroll performance */
  will-change: transform;
  transform: translateZ(0);

  @media (max-width: ${theme.breakpoints.mobile}) {
    /* Mobile: Full viewport height - card fills entire screen, tabs overlay transparently */
    height: 100dvh;
    min-height: 100dvh;
    max-height: 100dvh;
  }
`;

// Inner wrapper to ensure FeedItemRenderer fills the card
export const SingleCardInnerWrapper = styled.div`
  width: 100%;
  height: 100%;
`;

// Loading States
export const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: ${theme.colors.text.secondary};
  gap: ${theme.spacing.md};

  p {
    margin: 0;
    font-size: 0.9rem;
  }
`;

export const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid rgba(102, 126, 234, 0.3);
  border-top: 3px solid ${theme.colors.primary};
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

// Empty State
export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  text-align: center;
  color: ${theme.colors.text.secondary};
  padding: ${theme.spacing.xl};
`;

export const EmptyStateIcon = styled.div`
  font-size: 4rem;
  margin-bottom: ${theme.spacing.md};
  opacity: 0.5;
`;

export const EmptyStateTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0 0 ${theme.spacing.sm} 0;
  color: ${theme.colors.text.primary};
`;

export const EmptyStateDescription = styled.p`
  font-size: 1rem;
  margin: 0;
  max-width: 400px;
  line-height: 1.5;
  color: ${theme.colors.text.secondary};
`;

